/**
 * pages/CloseQuarter.tsx — carry a quarter's key results forward, then archive what is behind.
 *
 * ⚠️ THIS IS THE MOST CONSEQUENTIAL SCREEN IN THE APPLICATION, and it is built around one idea: the
 * person must be able to explore right up to the last press, and the last press must tell them exactly
 * what it is about to do. Everything before *Close the quarter* reads, resolves and previews; nothing
 * before it writes. `Check` resolves the objective they pasted and shows which quarter tag would be
 * used and where that tag came from — because a tag read off their own objective and a tag a model
 * proposed are different claims, and a person will rightly trust them differently.
 *
 * ⚠️ THE CONFIRMATION NAMES WHAT WILL BE ARCHIVED, IN NUMBERS, AND SAYS THAT IT IS REVERSIBLE. The
 * reference runbook calls positive-identity confirmation the one thing standing between a rollover and
 * the most destructive outcome available; archiving the wrong objective takes its children with it. It
 * also says, truthfully, that `goals_edit(archived: false)` puts it back and that Atlassian offers no
 * hard delete — a person who does not know that will assume the worst and not press the button they
 * meant to press.
 *
 * ⚠️ A HALTED RUN IS NOT AN ERROR STATE AND MUST NOT BE DRAWN AS ONE. Real goals were created; the
 * ledger describing them is the response. The screen renders what exists, names it by the key Atlassian
 * minted, and offers *Resume* — never *Try again*, which is the word that would invite somebody to
 * duplicate a quarter.
 */
import { useCallback, useState, type JSX } from "react";

import {
  Badge,
  Banner,
  Button,
  Card,
  Checkbox,
  DeniedState,
  ErrorState,
  LoadingState,
  Modal,
  Page,
  Table,
  TextField,
  useToast,
  type Column,
} from "../components/ui";
import {
  abandonClosure as abandonClosureRequest,
  ApiError,
  fetchClosure,
  goalErrorMessage,
  previewClosureTarget,
  resumeClosure as resumeClosureRequest,
  startClosure,
  submitClosure,
  type ClosureItemView,
  type ClosureResponse,
  type ClosureRunResponse,
  type ClosureTargetPreview,
  type QuarterTagSource,
} from "../lib/api";
import { formatRelative } from "../lib/format";

import { useAsync } from "./goalsShared";

export interface CloseQuarterPorts {
  readonly start: () => Promise<ClosureResponse>;
  readonly load: (closureId: string, signal: AbortSignal) => Promise<ClosureResponse>;
  readonly preview: (closureId: string, reference: string, quarterTag?: string) => Promise<ClosureTargetPreview>;
  readonly submit: (
    closureId: string,
    input: {
      readonly reference: string;
      readonly carryOverItemIds: readonly string[];
      readonly quarterTag?: string;
      readonly targetDate?: string;
    },
  ) => Promise<ClosureRunResponse>;
  readonly resume: (closureId: string) => Promise<ClosureRunResponse>;
  readonly abandon: (closureId: string) => Promise<void>;
}

export interface CloseQuarterProps {
  readonly buId: string;
  readonly goalId: string;
  /** From `/api/me`'s per-scope roles. Hiding is UX; the server re-checks `goal_author` on every write. */
  readonly canAuthor?: boolean;
  readonly ports?: CloseQuarterPorts;
}

/**
 * Where the quarter tag came from, in words a person can weigh.
 *
 * Deliberately not four synonyms for "we worked it out". `objective` is a fact about their own record;
 * `derived` is arithmetic on another fact; `model` is a guess that passed a shape check. The three
 * deserve three different amounts of trust and the screen says which one it is holding.
 */
const TAG_SOURCE_WORDS: Readonly<Record<QuarterTagSource, string>> = {
  objective: "read from next quarter's objective",
  derived: "the quarter after this one",
  model: "proposed by the model — worth a glance",
  person: "yours",
};

const CARRY_TONE: Readonly<Record<string, "neutral" | "info" | "success" | "warning" | "danger">> = {
  not_selected: "neutral",
  pending: "info",
  created: "info",
  carried: "success",
  failed: "danger",
  indeterminate: "warning",
};

function carryWord(item: ClosureItemView): string {
  if (!item.carryOver) return "Left behind";
  switch (item.carryState) {
    case "carried":
      return item.createdGoalKey === null ? "Carried over" : `Carried over as ${item.createdGoalKey}`;
    case "created":
      return item.createdGoalKey === null ? "Created, not finished" : `${item.createdGoalKey} — not finished`;
    case "failed":
      return "Could not be carried";
    case "indeterminate":
      return "Outcome unknown";
    default:
      return "To carry over";
  }
}

/**
 * The role gate, and it is a COMPONENT BOUNDARY rather than an early return, deliberately.
 *
 * ⚠️ It used to sit below the hooks, inside the body — and React runs a mount effect regardless of what
 * the render returned, so `useAsync` opened a closure for exactly the person the next line then refused.
 * Opening one is a WRITE to this application's own database (`POST …/closures`, which snapshots the
 * objective's key results), so the screen drew "you may not do this" and did a piece of it anyway, on
 * every arrival. The server refuses the write too — hiding is UX and `goal_author` is re-checked there —
 * but a screen that fires a write it has just declined to offer is not one whose "nothing before *Close
 * the quarter* writes" can be believed. An early return cannot skip a hook; a component can.
 */
export function CloseQuarter({ buId, goalId, canAuthor = false, ports }: CloseQuarterProps): JSX.Element {
  if (!canAuthor) {
    return (
      <Page title="Close quarter">
        <DeniedState body="Closing a quarter needs the goal-author role in this business unit. It is carried by a group, never by the account itself." />
      </Page>
    );
  }
  return <CloseQuarterBody buId={buId} goalId={goalId} {...(ports === undefined ? {} : { ports })} />;
}

function CloseQuarterBody({ buId, goalId, ports }: Omit<CloseQuarterProps, "canAuthor">): JSX.Element {
  const toast = useToast();

  const start = ports?.start ?? (() => startClosure(goalId));
  const loadOne = ports?.load ?? ((closureId: string, signal: AbortSignal) => fetchClosure(goalId, closureId, signal));
  const preview =
    ports?.preview ?? ((closureId: string, reference: string, tag?: string) => previewClosureTarget(goalId, closureId, reference, tag));
  const submit =
    ports?.submit ??
    ((closureId: string, input: { reference: string; carryOverItemIds: readonly string[]; quarterTag?: string; targetDate?: string }) =>
      submitClosure(goalId, closureId, input));
  const resume = ports?.resume ?? ((closureId: string) => resumeClosureRequest(goalId, closureId));
  const abandon = ports?.abandon ?? ((closureId: string) => abandonClosureRequest(goalId, closureId));

  // Opening a closure is a WRITE to this application's own database (never to Atlassian), so it happens
  // once on arrival and its answer is the page's state from then on. `useAsync` keys on the goal, so
  // navigating between objectives does not hand one objective's key results to another.
  const opening = useAsync(
    useCallback(() => start(), [goalId]),
    `closure:${buId}:${goalId}`,
  );

  const [state, setState] = useState<ClosureResponse | ClosureRunResponse | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<string> | null>(null);
  const [reference, setReference] = useState("");
  const [quarterTag, setQuarterTag] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [resolved, setResolved] = useState<ClosureTargetPreview | null>(null);
  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const current = state ?? opening.data;
  const closure = current?.closure ?? null;
  const items = current?.items ?? [];
  const outcome = current !== null && "outcome" in current ? current.outcome : null;
  const notice = current !== null && "notice" in current ? current.notice : null;
  const haltReason = closure?.haltedReason ?? null;

  // The selection lives in the page until the first write, then follows the ledger — which is what makes
  // the table honest after a halt: the ticks then describe what WAS carried, not what somebody intended.
  const isSelected = useCallback(
    (item: ClosureItemView): boolean => (selected === null ? item.carryOver : selected.has(item.id)),
    [selected],
  );
  const selectedIds = items.filter((item) => isSelected(item)).map((item) => item.id);

  const settled = closure !== null && (closure.state === "closed" || closure.state === "archiving");
  const halted = closure?.state === "halted";
  const editable = closure?.state === "open" && !busy;

  const toggle = (item: ClosureItemView, on: boolean): void => {
    const next = new Set(selectedIds);
    if (on) next.add(item.id);
    else next.delete(item.id);
    setSelected(next);
  };

  const failed = (cause: unknown): void => {
    const message = cause instanceof ApiError ? goalErrorMessage(cause) : "Something went wrong. Nothing was written.";
    setProblem(message);
  };

  const check = async (): Promise<void> => {
    if (closure === null) return;
    setChecking(true);
    setProblem(null);
    try {
      const answer = await preview(closure.id, reference, quarterTag === "" ? undefined : quarterTag);
      setResolved(answer);
      if (quarterTag === "" && answer.quarterTag !== null) setQuarterTag(answer.quarterTag);
      if (targetDate === "" && answer.suggestedTargetDate !== null) setTargetDate(answer.suggestedTargetDate);
    } catch (cause) {
      setResolved(null);
      failed(cause);
    } finally {
      setChecking(false);
    }
  };

  const run = async (): Promise<void> => {
    if (closure === null) return;
    setConfirming(false);
    setBusy(true);
    setProblem(null);
    try {
      const answer = await submit(closure.id, {
        reference,
        carryOverItemIds: selectedIds,
        ...(quarterTag === "" ? {} : { quarterTag }),
        ...(targetDate === "" ? {} : { targetDate }),
      });
      setState(answer);
      setSelected(null);
      if (answer.outcome === "closed") toast.show("The quarter is closed.", { tone: "success" });
      // ⚠️ NOT a danger toast. A halt is not a failure of the request — real goals were created, and the
      // banner below carries the detail. `info` keeps the toast from reading as "it all went wrong".
      else toast.show("The close stopped part-way. Nothing further was written.", { tone: "info" });
    } catch (cause) {
      failed(cause);
    } finally {
      setBusy(false);
    }
  };

  const pickUp = async (): Promise<void> => {
    if (closure === null) return;
    setBusy(true);
    setProblem(null);
    try {
      const answer = await resume(closure.id);
      setState(answer);
      if (answer.outcome === "closed") toast.show("The quarter is closed.", { tone: "success" });
    } catch (cause) {
      failed(cause);
    } finally {
      setBusy(false);
    }
  };

  const leave = async (): Promise<void> => {
    if (closure === null) return;
    try {
      await abandon(closure.id);
      window.location.hash = `#/goals/${encodeURIComponent(goalId)}`;
    } catch (cause) {
      failed(cause);
    }
  };

  const refresh = (): void => {
    if (closure === null) return;
    const controller = new AbortController();
    void loadOne(closure.id, controller.signal)
      .then((answer) => setState(answer))
      .catch((cause: unknown) => failed(cause));
  };

  const columns: readonly Column<ClosureItemView>[] = [
    {
      key: "carry",
      header: "Carry over",
      sortable: false,
      render: (row) => (
        <Checkbox
          label={`Carry ${row.keyResultKey} over`}
          checked={isSelected(row)}
          disabled={!editable}
          onChange={(on) => toggle(row, on)}
        />
      ),
    },
    {
      key: "name",
      header: "Key result",
      type: "text",
      value: (row) => row.name,
      render: (row) =>
        row.url === null ? (
          <>
            {row.name} <span className="muted">{row.keyResultKey}</span>
          </>
        ) : (
          <a href={row.url} target="_blank" rel="noopener noreferrer">
            {row.name} <span className="muted">{row.keyResultKey}</span>
          </a>
        ),
    },
    {
      key: "tags",
      header: "Tags",
      type: "text",
      value: (row) => row.tags.join(", "),
      render: (row) => (row.tags.length === 0 ? "None" : row.tags.join(", ")),
    },
    {
      key: "teams",
      header: "Teams",
      type: "text",
      value: (row) => row.teams.join(", "),
      render: (row) => (row.teams.length === 0 ? "None" : row.teams.join(", ")),
      hideOnMobile: true,
    },
    {
      key: "metric",
      header: "Metric",
      type: "text",
      value: (row) => row.metric?.name ?? "",
      // The figures are shown because the carry-over copies them, and a person about to duplicate a
      // number should be able to see the number. "Tracks nothing" is a fact about the goal, not a gap.
      render: (row) =>
        row.metric === null ? (
          "Tracks nothing"
        ) : (
          <>
            {row.metric.name}
            <span className="muted">
              {" "}
              {row.metric.current ?? "?"} → {row.metric.target ?? "?"}
            </span>
          </>
        ),
      hideOnMobile: true,
    },
    {
      key: "outcome",
      header: "Outcome",
      type: "text",
      value: (row) => row.carryState,
      render: (row) => (
        <>
          <Badge tone={row.carryOver ? (CARRY_TONE[row.carryState] ?? "neutral") : "neutral"}>{carryWord(row)}</Badge>
          {row.archiveState === "archived" ? <Badge tone="neutral">Archived</Badge> : null}
          {row.carryFailure === null ? null : <p className="field__hint">{row.carryFailure}</p>}
          {row.archiveFailure === null ? null : <p className="field__hint">{row.archiveFailure}</p>}
        </>
      ),
    },
  ];

  const carryingCount = selectedIds.length;
  const readyToSubmit = editable && resolved !== null && (carryingCount === 0 || quarterTag !== "");

  return (
    <Page
      title="Close quarter"
      {...(closure === null
        ? {}
        : {
            lede: `${items.length} key result${items.length === 1 ? "" : "s"} under this objective · opened ${formatRelative(closure.openedAt)}`,
          })}
    >
      <p>
        <Button variant="link" size="sm" onClick={() => (window.location.hash = `#/goals/${encodeURIComponent(goalId)}`)}>
          ← Back to the objective
        </Button>
      </p>

      {opening.loading ? <LoadingState label="Reading this objective's key results" /> : null}

      {opening.error !== null && current === null ? (
        <ErrorState
          message={opening.error instanceof ApiError ? goalErrorMessage(opening.error) : "This objective could not be read."}
          traceId={opening.error instanceof ApiError ? opening.error.traceId : null}
          onRetry={() => opening.reload()}
        />
      ) : null}

      {problem === null ? null : <Banner tone="danger">{problem}</Banner>}

      {notice === null ? null : <Banner tone="warning">{notice}</Banner>}
      {halted && haltReason !== null ? <Banner tone="warning">{haltReason}</Banner> : null}

      {outcome === "closed" ? (
        <Banner tone="info">
          This quarter is closed. Its key results and the objective itself are archived in Atlassian — which is
          reversible there, and nothing has been deleted.
        </Banner>
      ) : null}

      {closure === null ? null : (
        <>
          <Card
            title="What happens"
            support="Nothing below is written until you press Close the quarter."
          >
            <ol>
              <li>Each key result you tick is duplicated under next quarter&rsquo;s objective — its name, description, tags with the quarter swapped, its teams, and its metric.</li>
              <li>Only when every one of them has landed is this quarter&rsquo;s objective archived, along with all {items.length} of its key results.</li>
              <li>If anything fails, the run stops and <strong>nothing is archived</strong>. What was created is listed here and is never created twice.</li>
            </ol>
          </Card>

          <Card title="Key results">
            <Table
              caption="The key results under this objective, and whether each is carried into next quarter"
              columns={columns}
              rows={items}
              rowKey={(row) => row.id}
              defaultSort={{ key: "name", direction: "ascending" }}
              titleKey="name"
              mobile="cards"
              empty={{ title: "No key results", body: "This objective has no key results to carry or archive." }}
            />
          </Card>

          {settled || halted ? null : (
            <Card title="Next quarter objective">
              <TextField
                label="Next quarter objective"
                value={reference}
                onChange={(value) => {
                  // ⚠️ EDITING THE ADDRESS THROWS AWAY WHAT WAS CHECKED. `resolved` is what the
                  // confirmation names, and `run` submits this field — so leaving a stale `resolved` in
                  // place lets a person read "carrying into Grow the customer base (TROPI8-700)" and
                  // press yes on a write to whatever they typed afterwards. Positive-identity
                  // confirmation is the one control standing in front of this flow (see the header); a
                  // confirmation that can name a different objective from the one written to is not one.
                  setReference(value);
                  setResolved(null);
                }}
                type="url"
                inputMode="url"
                placeholder="https://home.atlassian.com/…/goal/TROPI8-667/about"
                hint="Paste the address of next quarter's objective from your browser, or type its key."
                disabled={!editable}
                required
              />
              <p>
                <Button variant="secondary" onClick={() => void check()} disabled={!editable || reference.trim() === "" || checking}>
                  {checking ? "Checking…" : "Check"}
                </Button>
              </p>

              {resolved === null ? null : (
                <Banner tone="info">
                  Carrying into <strong>{resolved.target.name}</strong> ({resolved.target.key}).{" "}
                  {resolved.quarterTag === null
                    ? "Which quarter this is could not be worked out — type the tag below."
                    : `Every carry-over will be tagged ${resolved.quarterTag} — ${resolved.quarterTagSource === null ? "source unknown" : TAG_SOURCE_WORDS[resolved.quarterTagSource]}.`}
                </Banner>
              )}

              <TextField
                label="Quarter tag"
                value={quarterTag}
                onChange={setQuarterTag}
                width="short"
                placeholder="2026-q4"
                hint="Applied to every carry-over. Other tags are copied across unchanged."
                disabled={!editable}
              />
              <TextField
                label="Target date"
                value={targetDate}
                onChange={setTargetDate}
                width="short"
                placeholder="2026-12-31"
                hint="Optional, written YYYY-MM-DD. Leave it blank and the carry-overs inherit no date."
                disabled={!editable}
              />
            </Card>
          )}

          <Card title={halted ? "Pick up where it stopped" : "Close the quarter"}>
            {halted ? (
              <>
                <p>
                  Everything shown as carried over already exists in Atlassian and will not be created again.
                  Resuming continues from the first thing that has not landed.
                </p>
                <Button variant="primary" onClick={() => void pickUp()} disabled={busy}>
                  {busy ? "Resuming…" : "Resume"}
                </Button>
                <Button variant="link" onClick={refresh} disabled={busy}>
                  Refresh
                </Button>
              </>
            ) : settled ? (
              <p>This close has finished. There is nothing further to do here.</p>
            ) : (
              <>
                <Button variant="primary" onClick={() => setConfirming(true)} disabled={!readyToSubmit || busy}>
                  {busy ? "Closing the quarter…" : "Close the quarter"}
                </Button>
                <Button variant="link" onClick={() => void leave()} disabled={busy}>
                  Abandon
                </Button>
                {resolved === null ? (
                  <p className="field__hint">Check next quarter&rsquo;s objective first.</p>
                ) : carryingCount > 0 && quarterTag === "" ? (
                  <p className="field__hint">A quarter tag is needed before anything can be carried over.</p>
                ) : null}
              </>
            )}
          </Card>
        </>
      )}

      {/*
        The positive-identity confirmation. It names the target by key AND name, states both numbers, and
        says archiving is reversible — because a person who does not know that will decline the button
        they meant to press, and one who wrongly believes it is permanent is being asked for consent to
        something other than what happens.
      */}
      <Modal
        open={confirming}
        role="alertdialog"
        title="Close this quarter?"
        description="This writes to Atlassian."
        onClose={() => setConfirming(false)}
        actions={
          // Cancel FIRST and the destructive action styled `danger`, per 03-components.md §4 ("cancel
          // then confirm"; "danger styling on confirm; cancel is the default focus") — and matching the
          // abandon dialog in `GoalCheckin.tsx`, which is the same shape of decision. `Modal` focuses
          // the first focusable descendant, so the order is not cosmetic: with the confirm first, a
          // keyboard user holding Enter opens this dialog and archives a quarter with the same keypress.
          <>
            <Button variant="ghost" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={() => void run()}>
              Yes — close the quarter
            </Button>
          </>
        }
      >
        <ul>
          <li>
            <strong>{carryingCount}</strong> key result{carryingCount === 1 ? "" : "s"} will be duplicated under{" "}
            <strong>{resolved?.target.name ?? "next quarter's objective"}</strong> ({resolved?.target.key ?? "?"})
            {quarterTag === "" ? null : (
              <>
                , tagged <strong>{quarterTag}</strong>
              </>
            )}
            .
          </li>
          <li>
            Then <strong>{items.length}</strong> key result{items.length === 1 ? "" : "s"} and this objective will be{" "}
            <strong>archived</strong>.
          </li>
          <li>Archiving can be undone in Atlassian, and nothing is deleted.</li>
          <li>If any step fails, the run stops and nothing is archived.</li>
        </ul>
      </Modal>
    </Page>
  );
}
